/**
 * Monthly working-hours config for Krasna Polyana Karting.
 * `times` follows the exact schedule supplied by the owner.
 *
 * Where a month has two windows (e.g. March), the FIRST entry is for
 * Mon–Fri and the SECOND is for Sat–Sun. A single-entry month means
 * the same hours apply seven days a week.
 *
 * Special case: September splits at the 15th — handled via `splitDay`.
 */
export const MONTHLY_HOURS = [
  { month: 1,  labelEn: "January",   labelBg: "Януари",    weekdays: "12:00 — 17:00", weekend: "12:00 — 17:00" },
  { month: 2,  labelEn: "February",  labelBg: "Февруари",  weekdays: "12:00 — 18:00", weekend: "12:00 — 18:00" },
  { month: 3,  labelEn: "March",     labelBg: "Март",      weekdays: "12:00 — 19:00", weekend: "11:00 — 19:00" },
  { month: 4,  labelEn: "April",     labelBg: "Април",     weekdays: "12:00 — 20:00", weekend: "11:00 — 20:00" },
  { month: 5,  labelEn: "May",       labelBg: "Май",       weekdays: "12:00 — 21:00", weekend: "10:00 — 21:00" },
  { month: 6,  labelEn: "June",      labelBg: "Юни",       weekdays: "11:00 — 21:00", weekend: "11:00 — 21:00" },
  { month: 7,  labelEn: "July",      labelBg: "Юли",       weekdays: "10:00 — 21:00", weekend: "10:00 — 21:00" },
  { month: 8,  labelEn: "August",    labelBg: "Август",    weekdays: "10:00 — 21:00", weekend: "10:00 — 21:00" },
  // September splits — until 15th vs. after
  {
    month: 9,
    labelEn: "September",
    labelBg: "Септември",
    splitDay: 15,
    early: { weekdays: "10:00 — 21:00", weekend: "10:00 — 21:00" },
    late:  { weekdays: "12:00 — 21:00", weekend: "10:00 — 21:00" },
  },
  { month: 10, labelEn: "October",   labelBg: "Октомври",  weekdays: "12:00 — 21:00", weekend: "10:00 — 21:00" },
  { month: 11, labelEn: "November",  labelBg: "Ноември",   weekdays: "12:00 — 20:00", weekend: "10:00 — 20:00" },
  { month: 12, labelEn: "December",  labelBg: "Декември",  weekdays: "12:00 — 18:00", weekend: "12:00 — 18:00" },
];

// Returns the active schedule for the given Date (defaults to today, Europe/Sofia
// — JS Date is already local; deployment runs on UTC but the format strings are
// purely informative, so no TZ conversion is needed here).
export const getCurrentSeason = (date = new Date()) => {
  const m = date.getMonth() + 1; // 1..12
  const d = date.getDate();
  const cfg = MONTHLY_HOURS.find((x) => x.month === m) || MONTHLY_HOURS[0];

  let weekdays = cfg.weekdays;
  let weekend = cfg.weekend;
  if (cfg.splitDay) {
    const slice = d <= cfg.splitDay ? cfg.early : cfg.late;
    weekdays = slice.weekdays;
    weekend = slice.weekend;
  }

  // Translate to the shape consumed by ContactPage/Footer.
  return {
    id: `m${cfg.month}`,
    labelEn: `${cfg.labelEn} schedule`,
    labelBg: `${cfg.labelBg} разписание`,
    hours: [
      { dayEn: "Mon — Fri", dayBg: "Пон — Пет", time: weekdays },
      { dayEn: "Sat — Sun", dayBg: "Съб — Нед", time: weekend },
    ],
    noteEn: "Racing only on dry track above 0°C. Closed only on Christmas. Always call to confirm.",
    noteBg: "Каране само на сухо и над 0°C. Затворено само на Коледа. Винаги звъни за потвърждение.",
  };
};

// Used by the new full-year hours table.
export const getAllMonthlyHours = (lang = "en") =>
  MONTHLY_HOURS.map((cfg) => {
    if (cfg.splitDay) {
      return {
        label: lang === "bg" ? cfg.labelBg : cfg.labelEn,
        rows: [
          {
            sub: lang === "bg" ? `до ${cfg.splitDay}-ти` : `until ${cfg.splitDay}th`,
            weekdays: cfg.early.weekdays,
            weekend: cfg.early.weekend,
          },
          {
            sub: lang === "bg" ? `след ${cfg.splitDay}-ти` : `after ${cfg.splitDay}th`,
            weekdays: cfg.late.weekdays,
            weekend: cfg.late.weekend,
          },
        ],
      };
    }
    return {
      label: lang === "bg" ? cfg.labelBg : cfg.labelEn,
      rows: [{ sub: null, weekdays: cfg.weekdays, weekend: cfg.weekend }],
    };
  });
